import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext, lazy, Suspense } from 'react';
import '@/App.css';
import './i18n';
import { setSentryUser } from './sentry';
import axios from 'axios';
import { Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      // Flush any captured ?ref=USER_ID share landings so the book
      // surfaces in the recipient's "Shared with me" page. Idempotent
      // and best-effort - silent on failure.
      try {
        const { flushPendingShareTracks } = await import('./lib/shareUtils');
        flushPendingShareTracks();
      } catch (_e) { /* noop */ }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
    } catch (error) {
      // Logout is best-effort; clear client state even if the request fails.
      setUser(null);
    }
  }, []);

  // NOTE (Feb 2026): The 5-minute idle auto-logout was removed at
  // user request - admins were getting bumped out mid-moderation. We
  // now keep the session alive until the user explicitly logs out,
  // the cookie expires, or the browser tab is closed.

  // Push the current user into Sentry whenever it changes (no-op if DSN empty).
  useEffect(() => {
    setSentryUser(user);
  }, [user]);

  const contextValue = useMemo(
    () => ({ user, setUser, loading, checkAuth, logout }),
    [user, loading, checkAuth, logout]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment (not query params) for session_id
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/book/:id" element={<BookDetail />} />
        <Route path="/read/:id" element={<ProtectedRoute><BookReader /></ProtectedRoute>} />
        <Route path="/create" element={<CreateStorybook />} />
        <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
        <Route path="/checkout/success" element={<ProtectedRoute><CheckoutSuccess /></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
        <Route path="/seller/dashboard" element={<ProtectedRoute><SellerDashboard /></ProtectedRoute>} />
        <Route path="/seller/analytics" element={<ProtectedRoute><CreatorAnalytics /></ProtectedRoute>} />
        <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
        <Route path="/gifts" element={<ProtectedRoute><Gifts /></ProtectedRoute>} />
        <Route path="/redeem" element={<ProtectedRoute><RedeemGift /></ProtectedRoute>} />
        <Route path="/profile/:userId" element={<CreatorProfile />} />
        <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
        <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/growth-os" element={<ProtectedRoute><GrowthOsAdmin /></ProtectedRoute>} />
        <Route path="/admin/gsc" element={<ProtectedRoute><GscAdmin /></ProtectedRoute>} />
        <Route path="/academy" element={<Academy />} />
        <Route path="/academy/:category" element={<Academy />} />
        <Route path="/academy/:category/:slug" element={<AcademyArticle />} />
        <Route path="/storybooks" element={<SeoStorybookHub />} />
        <Route path="/storybooks/:slug" element={<ProgrammaticSeoPage />} />
        <Route path="/ref/:code" element={<RefLanding />} />
        <Route path="/safety" element={<SafetyPage />} />
        <Route path="/founder" element={<FounderPage />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsConditions />} />
        <Route path="/my/feedback" element={<ProtectedRoute><MyFeedback /></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
}

function AuthCallback() {
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        window.location.href = '/login';
        return;
      }

      try {
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        setUser(response.data);
        window.location.href = '/dashboard';
      } catch (error) {
        window.location.href = '/login';
      }
    };

    processSession();
  }, [setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFDF9' }}>
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#FF8E71' }}></div>
        <p className="mt-4" style={{ color: '#2D3748' }}>Authenticating...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);

  useEffect(() => {
    if (location.state?.user) return;

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (response.data) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
      }
    };

    if (!loading) {
      checkAuth();
    }
  }, [location.state, loading]);

  if (loading || isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFDF9' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#FF8E71' }}></div>
          <p className="mt-4" style={{ color: '#2D3748' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user && isAuthenticated === false) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Phase 119.14 - route-level code splitting.
// Phase 123 - only the LCP-critical LandingPage stays eager; every
// other route (including public content pages) is its own lazy chunk
// so a cold mobile first-view downloads the minimum JS (PageSpeed
// "reduce unused JavaScript" fix).
import LandingPage from './pages/LandingPage';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const BookDetail = lazy(() => import('./pages/BookDetail'));
const CreatorProfile = lazy(() => import('./pages/CreatorProfile'));
const RefLanding = lazy(() => import('./pages/RefLanding'));
const SafetyPage = lazy(() => import('./pages/SafetyPage'));
const FounderPage = lazy(() => import('./pages/FounderPage'));
const FAQ = lazy(() => import('./pages/FAQ'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./pages/TermsConditions'));
const Academy = lazy(() => import('./pages/Academy'));
const AcademyArticle = lazy(() => import('./pages/AcademyArticle'));
const SeoStorybookHub = lazy(() => import('./pages/SeoStorybookHub'));
const ProgrammaticSeoPage = lazy(() => import('./pages/ProgrammaticSeoPage'));

const Dashboard = lazy(() => import('./pages/Dashboard'));
const BookReader = lazy(() => import('./pages/BookReader'));
const CreateStorybook = lazy(() => import('./pages/CreateStorybook'));
const Cart = lazy(() => import('./pages/Cart'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const Orders = lazy(() => import('./pages/Orders'));
const SellerDashboard = lazy(() => import('./pages/SellerDashboard'));
const CreatorAnalytics = lazy(() => import('./pages/CreatorAnalytics'));
const WalletPage = lazy(() => import('./pages/WalletPage'));
const Gifts = lazy(() => import('./pages/Gifts'));
const RedeemGift = lazy(() => import('./pages/RedeemGift'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const Subscription = lazy(() => import('./pages/Subscription'));
const Settings = lazy(() => import('./pages/Settings'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const GrowthOsAdmin = lazy(() => import('./pages/admin/GrowthOsAdmin'));
const GscAdmin = lazy(() => import('./pages/admin/GscAdmin'));
const MyFeedback = lazy(() => import('./pages/MyFeedback'));

// Lightweight branded spinner shown while a lazy chunk loads.
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFDF9' }}>
      <div
        className="animate-spin rounded-full h-10 w-10 border-b-2"
        style={{ borderColor: '#FF8E71' }}
        aria-label="Loading page"
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
